import {
	AfterViewInit,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	OnInit,
	output,
	QueryList,
	signal,
	ViewChildren,
	WritableSignal,
	ChangeDetectionStrategy,
	input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	BillingPeriod,
	CostGroup,
	CostGroupCategory,
	CostGroupRequest,
	CostGroupService,
	CostGroupType,
	CostGroupTypeService,
	CostTemplateService,
} from '@eadsysteme/billingclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';
import { fadeInUp400ms } from '../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../@vex/animations/stagger.animation';
import { BreadCrumbService } from '../../../../../@vex/components/breadcrumbs/BreadCrumbService';
import { DateUtil } from '../../../../util/DateUtil';
import { Icons } from '../../../../util/Icons';
import { TableColumn } from '../../../../../@vex/interfaces/table-column.interface';
import { SessionService } from '../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, zip } from 'rxjs';
import { PeriodService } from '../period.service';
import { InvoiceDetailService } from './cost/invoice/invoice-detail.service';
import { MatList, MatListItem } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { CostComponent } from './cost/cost.component';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { AsyncPipe } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CostGroupDialogComponent } from './add-cost-dialog/cost-group-dialog.component';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { CostGroupTypeDialogComponent } from './cost-group-type-dialog/cost-group-type-dialog.component';
import { LocaleHelper } from '../../../../util/LocaleHelper';
import { SnackbarService } from '../../../../services/snackbar.service';
import { CookieService } from 'ngx-cookie-service';
import { FlatrateExemptionService } from './flatrates-exemptions/flatrates-exemption.service';

import { AraPopoverComponent } from '../../../util/ara-popover/popover.component';

@Component({
	selector: 'vex-costs',
	templateUrl: './costs.component.html',
	styleUrl: './costs.component.scss',
	imports: [
		TranslocoDirective,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		ReactiveFormsModule,
		MatList,
		MatListItem,
		MatIcon,
		MatTabGroup,
		MatTab,
		AsyncPipe,
		MatMenu,
		MatMenuTrigger,
		MatButton,
		CdkDropList,
		CdkDrag,
		MatProgressSpinner,
		CostComponent,
		MatTabLabel,
		MatTooltip,
		MatMenuItem,
		MatMenuContent,
		MatIconButton,
		MatSelectTrigger,
		AraPopoverComponent,
		MatSlideToggle,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class CostsComponent implements OnInit, AfterViewInit {
	protected breadCrumbService = inject(BreadCrumbService);
	protected dateUtil = inject(DateUtil);
	protected icons = inject(Icons);
	protected invoiceDetailService = inject(InvoiceDetailService);
	private readonly translocoService = inject(TranslocoService);
	private readonly sessionService = inject(SessionService);
	private readonly costGroupService = inject(CostGroupService);
	private readonly costTemplateService = inject(CostTemplateService);
	private readonly costGroupTypeService = inject(CostGroupTypeService);
	protected readonly selectedPeriodService = inject(PeriodService);
	private readonly dialog = inject(MatDialog);

	private readonly destroyRef = inject(DestroyRef);
	protected flatrateExemptionService = inject(FlatrateExemptionService);

	@ViewChildren('costGroupItem', { read: ElementRef }) costGroupItems: QueryList<ElementRef>;
	private scrollToLastCostGroupOfType: CostGroupType;

	// TODO(billingclient-v637): BillingVersion-Typ existiert in der neuen API nicht mehr — Verwendung klären.
	versions: Array<unknown> = [];
	finishedLoading = signal(true);
	pageIndex: number = 0;
	pageSize: number = 20;
	length: number = 0;
	pageSizeOptions: number[] = [10, 20, 50, 100];

	protected selectedTabIndex = signal<number>(0);
	protected invoiceViewMode = signal<'table' | 'card'>('card');

	periodFc: FormControl = new FormControl<BillingPeriod>({ value: undefined, disabled: true });
	readonly billingId = input.required<string>();

	readonly columnsCost = input<TableColumn<string>[]>([
		{
			label: this.translocoService.translate('billing.name'),
			property: 'name',
			type: 'text',
			visible: true,
		},
		{
			label: this.translocoService.translate('billing.description'),
			property: 'description',
			type: 'text',
			visible: true,
		},
		{
			label: this.translocoService.translate('billing.abbreviation'),
			property: 'abbreviation',
			type: 'text',
			visible: true,
		},
	]);
	readonly periods = input<BillingPeriod[]>(undefined);

	protected searchFilterSubject: Subject<string> = new Subject<string>();
	protected searchFilter$: Observable<string> = this.searchFilterSubject.asObservable();

	protected costTabs = output<any>(); //invoiceId + periodId
	navigateToAllocations = output<void>();
	protected costGroups: WritableSignal<CostGroup[]> = signal<CostGroup[]>([]);
	protected costGroupTypes = signal<CostGroupType[]>([]);
	protected matchingCostGroups: Map<CostGroupType, CostGroup[]> = new Map<CostGroupType, CostGroup[]>();
	protected selectedCostGroupType: WritableSignal<CostGroupType> = signal(undefined);
	private readonly snackBarService = inject(SnackbarService);
	private readonly cookieService = inject(CookieService);
	protected costsExpandedSubject = new Subject<boolean>();
	previousCostGroups = signal<CostGroup[]>([]);

	ngOnInit(): void {
		// Sicherstellen, dass immer ein Zeitraum ausgewählt ist, wenn Zeiträume vorhanden sind
		const periods = this.periods();
		if (this.selectedPeriodService.selectedPeriod()) {
			// Finde das entsprechende BillingPeriod-Objekt aus dem periods-Array (für korrekte Referenz-Gleichheit im mat-select)
			const selectedPeriodId = this.selectedPeriodService.selectedPeriod().exportId;
			const matchingPeriod = this.periods()?.find((p) => p.exportId === selectedPeriodId);
			const selectedPeriod = matchingPeriod || this.selectedPeriodService.selectedPeriod();
			if (matchingPeriod) {
				this.selectedPeriodService.selectedPeriod.set(matchingPeriod);
			}
			this.periodFc.setValue(selectedPeriod);
			this.periodFc.enable();
			this.loadPeriodData(selectedPeriod);
		} else if (periods && periods.length > 0) {
			const selectedPeriod = periods[0];
			this.selectedPeriodService.selectedPeriod.set(selectedPeriod);
			this.periodFc.setValue(selectedPeriod);
			this.periodFc.enable();
			this.loadPeriodData(selectedPeriod);
		} else {
			// Kein Zeitraum vorhanden - FormControl bleibt disabled
			this.periodFc.disable();
		}

		this.flatrateExemptionService.showDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (value) => {
				if (!value) {
					this.initPeriod();
				}
			},
		});
	}

	ngAfterViewInit() {
		this.costGroupItems.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			if (this.scrollToLastCostGroupOfType?.exportId && this.costGroupItems) {
				// must wait for the data to be fully loaded AND rendered
				if (!this.finishedLoading()) {
					return;
				}
				const itemsInCurrentTab = this.costGroupItems.filter(
					(elRef: ElementRef) => elRef.nativeElement.dataset.costGroupTypeId === this.scrollToLastCostGroupOfType?.exportId
				);

				if (itemsInCurrentTab.length > 0) {
					const lastItemInTab = itemsInCurrentTab.at(-1);
					setTimeout(() => {
						lastItemInTab.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
					}, 1000); // scheint lang zu sein, ist aber erforderlich, um zu warten, bis alle Kostengruppen, die erweitert werden sollen, vollständig erweitert sind, da dies ebenfalls animiert wird
				}
				this.scrollToLastCostGroupOfType = undefined;
			}
		});
	}

	loadPeriod(event: MatSelectChange): void {
		this.finishedLoading.set(false);

		const selectedPeriod = event.value;
		this.selectedPeriodService.selectedPeriod.set(selectedPeriod);
		this.periodFc.setValue(selectedPeriod);

		this.loadPeriodData(selectedPeriod);
	}

	loadPeriodData(selectedPeriod: BillingPeriod): void {
		const previousPeriod = this.getPreviousPeriod();

		if (previousPeriod && previousPeriod.vatType === selectedPeriod.vatType) {
			this.costGroupService
				.readAllCostGroup(this.sessionService.effectiveArea, this.billingId(), previousPeriod.exportId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (previousCostGroups) => {
						this.previousCostGroups.set(previousCostGroups || []);
						this.initPeriod();
					},
					error: () => {
						this.previousCostGroups.set([]);
						this.initPeriod();
					},
				});
		} else {
			this.previousCostGroups.set([]);
			this.initPeriod();
		}
	}

	initPeriod() {
		this.finishedLoading.set(false);
		zip(
			this.costGroupService.readAllCostGroup(this.sessionService.effectiveArea, this.billingId(), this.selectedPeriodService.selectedPeriod().exportId),
			this.costGroupTypeService.readAllCostGroupType(this.sessionService.effectiveArea)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.pipe(
				finalize(() => {
					this.finishedLoading.set(true);
				})
			)
			.subscribe({
				next: ([responseCostGroups, responseCostTypes]: [CostGroup[], CostGroupType[]]) => {
					const costGroups = [...responseCostGroups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
					const costTypes = responseCostTypes;

					this.costGroups.set(costGroups);
					const sortedCostTypes: CostGroupType[] = [];
					for (const value of costTypes) {
						if (value.category === CostGroupCategory.HEAT_ENERGY) {
							sortedCostTypes.splice(0, 0, value);
						} else if (value.category === CostGroupCategory.HEATING_OPERATION) {
							sortedCostTypes.splice(1, 0, value);
						} else if (value.category === CostGroupCategory.ANCILLARY_SERVICES) {
							sortedCostTypes.splice(2, 0, value);
						} else {
							sortedCostTypes.push(value);
						}
					}
					this.costGroupTypes.set(sortedCostTypes);

					this.matchingCostGroups.clear();
					for (const cg of this.costGroups()) {
						const type = this.costGroupTypes().find((t) => t.exportId === cg.costGroupTypeExportId);
						if (type) {
							if (!this.matchingCostGroups.has(type)) {
								this.matchingCostGroups.set(type, []);
							}
							this.matchingCostGroups.get(type).push(cg);
						}
					}
				},
			});
	}

	getPreviousPeriod(): BillingPeriod | undefined {
		const currentPeriod = this.periodFc.value;
		if (!currentPeriod?.previousBillingPeriod?.exportId) {
			return undefined;
		}

		// Finde die previousPeriod aus der periods Liste
		return this.periods().find((p) => p.exportId === currentPeriod.previousBillingPeriod?.exportId);
	}

	addCostGroup(currentCostGroupType: CostGroupType) {
		const currentTabIndex = this.costGroupTypes().findIndex((type) => type.exportId === currentCostGroupType.exportId);

		const matDialogRef = this.dialog.open(CostGroupDialogComponent, {
			disableClose: false,
			width: '670px',
			data: {
				billingId: this.billingId(),
				billingPeriodId: this.periodFc.value?.exportId,
				currentCostGroupType: currentCostGroupType,
			},
		});

		matDialogRef.afterClosed().subscribe({
			next: (result) => {
				if (result?.saved) {
					this.selectedTabIndex.set(currentTabIndex);
					this.scrollToLastCostGroupOfType = currentCostGroupType;
					this.initPeriod();
				}
			},
		});
	}

	setSelectedTabIndex(costGroupTypeId: string) {
		const index = this.costGroupTypes().findIndex((type) => type.exportId === costGroupTypeId);
		if (index >= 0) {
			this.selectedTabIndex.set(index);
		}
	}

	drop(event: CdkDragDrop<CostGroup[]>, costGroupType: CostGroupType, menuTrigger: MatMenuTrigger) {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const matchingCosts = [...this.matchingCostGroups.get(costGroupType)];
		moveItemInArray(matchingCosts, event.previousIndex, event.currentIndex);

		// Update orders and prepare requests
		const updatedMatchingCosts = matchingCosts.map((cg, index) => {
			return { ...cg, order: index + 1 };
		});

		// Update matchingCostGroups immediately for the menu UI
		this.matchingCostGroups.set(costGroupType, updatedMatchingCosts);

		// Update main costGroups signal to refresh the big list UI
		this.costGroups.update((groups) => {
			const otherGroups = groups.filter((g) => g.costGroupTypeExportId !== costGroupType.exportId);
			return [...otherGroups, ...updatedMatchingCosts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		});

		const requests = updatedMatchingCosts.map((cg) =>
			// Vollständige CostGroup senden — sonst gehen invoiceCategories/-Deliveries/-Amounts beim Umsortieren verloren.
			this.costGroupService.updateCostGroup(this.sessionService.effectiveArea, this.billingId(), cg.exportId, cg as CostGroupRequest)
		);

		zip(...requests)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackBarService.success(this.translocoService.translate('billing.changedCostGroupOrder'));
					menuTrigger.closeMenu();
					this.initPeriod();
				},
				error: (err) => {
					const currentLanguage = LocaleHelper.localeToLanguage(this.cookieService.get('locale'));
					this.snackBarService.error(err.error.errorDescription.errorDetails[currentLanguage]);
					this.initPeriod(); // Refresh to revert to last known good state
				},
			});
	}

	countFor(type: CostGroupType): number {
		return this.matchingCostGroups.get(type)?.length ?? 0;
	}

	editCostGroupType(_: Event): void {
		// TODO(billingclient-v637): CostGroupType besitzt kein readOnly mehr — Quelle der Lock-Information klären.
		if ((this.selectedCostGroupType() as CostGroupType & { readOnly?: boolean })?.readOnly) {
			return;
		}
		this.dialog.open(CostGroupTypeDialogComponent, {
			disableClose: true,
			width: '670px',
			data: {
				billingId: this.billingId(),
				billingPeriodId: this.periodFc.value?.exportId,
				costGroupType: this.selectedCostGroupType,
			},
		});
	}

	deleteCostGroupType(_: Event): void {
		// TODO(billingclient-v637): CostGroupType besitzt kein readOnly mehr — Quelle der Lock-Information klären.
		if ((this.selectedCostGroupType() as CostGroupType & { readOnly?: boolean })?.readOnly) {
			return;
		}
	}

	buildCostGroupTypeTooltip(type: CostGroupType): string {
		const t = (k: string) => this.translocoService.translate(k);

		const norm = (v: unknown): string => {
			if (v === null || v === undefined) return '–';
			const s = String(v).replaceAll(/\s+/g, ' ').trim();
			return s.length ? s : '–';
		};

		const safeT = (key: string, fallback: string): string => {
			const out = t(key);
			if (!out || out === key) return fallback;
			const cleaned = out.replaceAll(/\s+/g, ' ').trim();
			return cleaned.length ? cleaned : fallback;
		};

		const categoryPretty = type?.category ? String(type.category).replaceAll('_', ' ') : '–';
		const category = type?.category ? safeT('enum.' + type.category, categoryPretty) : '–';

		const lines: string[] = [
			`${t('billing.costGroupType.name')}: ${norm(type?.name)}`,
			`${t('billing.costGroupType.printName')}: ${norm(type?.printname)}`,
			`${t('billing.costGroupType.abbreviation')}: ${norm(type?.abbreviation)}`,
			`${t('billing.costType')}: ${category}`,
			`${t('billing.costGroupType.description')}: ${norm(type?.description)}`,
		];

		return lines.join('\n');
	}

	updateCostGroup(index: number, updatedCostGroup: CostGroup | null) {
		const oldCostGroup = this.costGroups()[index];

		this.costGroups.update((groups) => {
			const newGroups = [...groups];
			if (updatedCostGroup) {
				newGroups[index] = updatedCostGroup;
			} else {
				newGroups.splice(index, 1);
			}
			return newGroups;
		});

		// Also update matchingCostGroups map for countFor() to work correctly
		if (oldCostGroup) {
			const type = this.costGroupTypes().find((t) => t.exportId === oldCostGroup.costGroupTypeExportId);
			if (type && this.matchingCostGroups.has(type)) {
				const currentList = this.matchingCostGroups.get(type);
				if (updatedCostGroup) {
					// Update existing entry
					const idx = currentList.findIndex((cg) => cg.exportId === updatedCostGroup.exportId);
					if (idx !== -1) {
						currentList[idx] = updatedCostGroup;
					}
				} else {
					// Remove deleted entry
					const filtered = currentList.filter((cg) => cg.exportId !== oldCostGroup.exportId);
					this.matchingCostGroups.set(type, filtered);
				}
			}
		}
	}

	protected collapseAllCostGroups() {
		// subject used so that every click is triggered even with the same value
		this.costsExpandedSubject.next(false);
	}

	protected expandAllCostGroups() {
		this.costsExpandedSubject.next(true);
	}

	get tooltipContent() {
		return this.translocoService.translateObject('billing.costAllocationInfoTooltip');
	}
}
